import React from 'react'
import styled from 'styled-components'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import { H1 } from '@jarvis.oddle/core/components/Typo'
import {
  colors,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { Column, Grid, Row } from '@jarvis.oddle/core/components/Grid'
import Image from '@jarvis.oddle/core/components/Image'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import richTextHtmlParser from '@jarvis.oddle/core/components/TextEditor/parser'
import { selectSections } from 'src/redux/customisation'
import useAppSelector from 'src/hooks/useAppSelector'
import ResponsiveSection from 'src/components/core/ResponsiveSection'

const Container = styled(Div)`
  background-color: ${(props) =>
    props.theme.colorContainerColor || colors.container};
  margin: ${margin[marginSizes.large]} 0;
  position: relative;
`
const ContentRow = styled(Row)<{
  desktopReverse: boolean
}>`
  min-height: 450px;
  box-sizing: border-box;
  ${breakpoint('desktop')`
    ${(props) =>
      props.desktopReverse
        ? `
      flex-direction: row-reverse;
    `
        : ''}
  `}
`

// https://stackoverflow.com/questions/8673356/ie-doesnt-support-height-auto-for-images-what-should-i-use
// Fix for invalid height on IE
const StyledImage = styled(Image)`
  min-height: 1px;
`

const ColumnContent = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'center'
}))`
  height: 100%;
  padding: ${padding[paddingSizes.medium]} 0;
`

const defaultImageSource =
  'https://ucarecdn.com/eea6359c-8f72-4913-a4c3-41384e611c89/-/preview/'

interface ImageWithTextSection {
  id: string
  name: string
  type: string
  settings: {
    showImage: boolean
    imageSource: string
    imagePosition: string
    textAlignment: string
    bodyText: string
    headerText: string
  }
  blocks?: {
    [key: string]: unknown
  }
  blocksOrder?: string[]
  options: {
    visibleForPreview: boolean
  }
  updatedAt: string
}

interface ImageWithTextProps {
  id: string
}

const ImageWithText = ({ id }: ImageWithTextProps): JSX.Element => {
  const { settings } = useAppSelector(selectSections)[
    id
  ] as ImageWithTextSection
  const {
    headerText,
    bodyText,
    showImage,
    imageSource: _imageSource,
    textAlignment,
    imagePosition
  } = settings
  const imageSource = _imageSource || defaultImageSource
  const desktopColumnSizes = showImage ? 6 : 12
  const richText = richTextHtmlParser(bodyText)
  return (
    <Container>
      <ResponsiveSection>
        <ResponsiveSection.Container height='100%'>
          <Grid>
            <ContentRow desktopReverse={imagePosition === 'right'}>
              {showImage && (
                <Column sm={12} md={12} lg={desktopColumnSizes}>
                  <ColumnContent>
                    <StyledImage
                      alt={headerText}
                      src={imageSource}
                      width='100%'
                    />
                  </ColumnContent>
                </Column>
              )}
              <Column sm={12} md={12} lg={desktopColumnSizes}>
                <ColumnContent>
                  <H1 bold textAlign={textAlignment}>
                    {headerText}
                  </H1>
                  {richText}
                </ColumnContent>
              </Column>
            </ContentRow>
          </Grid>
        </ResponsiveSection.Container>
      </ResponsiveSection>
    </Container>
  )
}

export default ImageWithText
